import { useState, useRef, useEffect, useMemo } from 'react';

interface UseScrollProps {
    containerRef: React.RefObject<HTMLElement>;
    behavior?: ScrollBehavior;
}

interface ScrollState {
    x: number;
    y: number;
    setPosition: (position: { x: number; y: number }) => void;
    behavior: ScrollBehavior;
    setBehavior: (behavior: ScrollBehavior) => void;
    isScrolling: boolean;
    topArrived: boolean;
    rightArrived: boolean;
    bottomArrived: boolean;
    leftArrived: boolean;
    scrollingUp: boolean;
    scrollingLeft: boolean;
    scrollingRight: boolean;
    scrollingDown: boolean;
}

let lastScrollPosition = { x: 0, y: 0 };
export function useScroll({ behavior = 'smooth', containerRef }: UseScrollProps): ScrollState {
    const [scrollPosition, setScrollPosition] = useState({ x: 0, y: 0 });
    const [scrollBehavior, setScrollBehavior] = useState<ScrollBehavior>(behavior);
    const positionChange = useRef<any>(null);
    const [isScrolling, setIsScrolling] = useState(false);
    const [direction, setDirection] = useState({
        up: false,
        down: false,
        left: false,
        right: false,
    });

    useEffect(() => {
        const scrollEnd = () => {
            setDirection({
                up: false,
                down: false,
                left: false,
                right: false,
            });
            setIsScrolling(false);
            positionChange.current && clearInterval(positionChange.current);
        };
        const scrolling = () => {
            setDirection({
                up: (containerRef.current as HTMLElement)?.scrollTop < lastScrollPosition.y,
                down: (containerRef.current as HTMLElement)?.scrollTop > lastScrollPosition.y,
                left: (containerRef.current as HTMLElement)?.scrollLeft < lastScrollPosition.x,
                right: (containerRef.current as HTMLElement)?.scrollLeft > lastScrollPosition.x,
            });
            lastScrollPosition = {
                x: (containerRef.current as HTMLElement)?.scrollLeft || 0,
                y: (containerRef.current as HTMLElement)?.scrollTop || 0,
            };
            setTimeout(() => {
                scrollEnd();
            }, 100);
        };

        if (containerRef.current) {
            containerRef.current.addEventListener('scroll', scrolling);
        }

        return () => {
            if (containerRef.current) {
                containerRef.current.removeEventListener('scroll', scrolling);
            }
        };
    }, [containerRef]);

    function setPosition({ x, y }: { x: number; y: number }) {
        if (containerRef && containerRef.current) {
            containerRef.current.scrollTo({ top: y, left: x, behavior: scrollBehavior });
            positionChange.current = setInterval(() => {
                setScrollPosition({
                    x: containerRef.current?.scrollLeft || 0,
                    y: containerRef.current?.scrollTop || 0,
                });
            }, 50);
        }
    }

    const topArrived = useMemo(() => scrollPosition.y === 0, [scrollPosition.y]);
    const leftArrived = useMemo(() => scrollPosition.x === 0, [scrollPosition.x]);
    const bottomArrived = useMemo(
        () =>
            scrollPosition.y ===
            Number(containerRef.current?.scrollHeight) - Number(containerRef.current?.offsetHeight),
        [scrollPosition.y],
    );
    const rightArrived = useMemo(
        () =>
            scrollPosition.x === Number(containerRef.current?.scrollWidth) - Number(containerRef.current?.offsetWidth),
        [scrollPosition.x],
    );

    return {
        x: scrollPosition.x,
        y: scrollPosition.y,
        setPosition,
        behavior: scrollBehavior,
        setBehavior: setScrollBehavior,
        isScrolling,
        topArrived,
        rightArrived,
        bottomArrived,
        leftArrived,
        scrollingUp: direction.up,
        scrollingLeft: direction.left,
        scrollingRight: direction.right,
        scrollingDown: direction.down,
    };
}
